import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { StatusBadge } from "@/components/shared";
import { money, fmtDate, timeAgo, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Lock, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import FileManager from "@/components/files/FileManager";
import AgreementsPanel from "@/components/clients/AgreementsPanel";
import { ContactsPanel, CommunicationsPanel } from "@/components/clients/ClientPanels";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/clients/${id}/overview`); setD(data); }
    catch (e) { toast.error(apiErr(e)); navigate("/clients"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  const c = d.client;
  if (d.masked) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate("/clients")}
          data-testid="masked-client-back"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Clients
        </button>
        <Card className="p-6" data-testid="masked-client-preview">
          <div className="flex items-start gap-4">
            <div
              className="grid h-12 w-12 place-items-center rounded-xl text-white font-display font-bold"
              style={{ background: "#255058" }}
            >
              {initials(c.company_name)}
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">{c.company_name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {c.industry || "Industry not shared"} · {c.region || "—"}
              </p>
              <StatusBadge value={c.status || "active"} className="mt-3" />
            </div>
          </div>
          <div className="mt-6 border-l-2 border-amber-400 pl-3 text-sm text-muted-foreground">
            This is a restricted client preview. Contacts, ownership, activity, documents, and
            commercial details are not available.
          </div>
        </Card>
      </div>
    );
  }
  const hasCommercial = "invoices" in d;
  const TABS = [["overview", "Overview"], ["jobs", "Jobs"], ["contacts", "Contacts"],
    ["communications", "Communications"],
    ...(hasCommercial ? [["commercial", "Agreements & Rates"], ["revenue", "Revenue"]] : []), ["documents", "Documents"], ["activity", "Activity"]];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Clients</button>
      <Card className="p-6" data-testid="client-profile-header">
        <div className="flex flex-wrap items-start gap-5">
          <div className="h-16 w-16 rounded-xl grid place-items-center text-white font-display font-bold text-lg" style={{ background: c.logo_color || "#255058" }}>{c.logo_symbol || initials(c.company_name)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap"><h1 className="font-display text-2xl font-bold tracking-tight">{c.company_name}</h1><StatusBadge value={c.tier} /><StatusBadge value={c.health} /></div>
            <div className="text-muted-foreground mt-1">{c.industry} · {c.region}</div>
            <div className="flex flex-wrap gap-6 mt-3 text-sm">
              <Stat label="Active Jobs" value={d.counts.jobs} /><Stat label="Placements" value={d.counts.placements} />
              <Stat label="Contractors" value={d.counts.contractors} />
              <Stat label="Lifetime Revenue" value={money(d.lifetime_revenue, c.currency)} />
              <Stat label="Outstanding" value={money(d.outstanding, c.currency)} />
            </div>
          </div>
        </div>
      </Card>
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`client-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
        <TabsContent value="overview" className="mt-4"><Card className="p-5"><dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Row k="Legal Name" v={c.legal_name} /><Row k="Website" v={c.website} /><Row k="Domain" v={c.domain} />
          <Row k="Industry" v={c.industry} /><Row k="Location" v={c.location} /><Row k="Region" v={c.region} />
          <Row k="Email" v={c.email} /><Row k="Phone" v={c.phone} /><Row k="Owner" v={c.owner_name} />
          <Row k="Currency" v={c.currency} /><Row k="Status" v={c.status} /><Row k="Last Activity" v={fmtDate(c.last_activity_at)} />
        </dl></Card></TabsContent>
        <TabsContent value="jobs" className="mt-4"><Card className="overflow-hidden">{d.jobs.length ? <div className="divide-y divide-border">{d.jobs.map((j) => (
          <div key={j.id} className="flex items-center gap-3 p-3 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`/jobs/${j.id}`)}><Building2 className="h-4 w-4 text-[#255058]" /><div className="flex-1"><div className="font-medium text-sm">{j.title}</div><div className="text-xs text-muted-foreground">{j.requisition_id} · {j.location}</div></div><StatusBadge value={j.status} /></div>
        ))}</div> : <div className="p-6 text-sm text-muted-foreground text-center">No jobs.</div>}</Card></TabsContent>
        <TabsContent value="contacts" className="mt-4">
          <ContactsPanel clientId={id} contacts={d.contacts} canWrite={can("client:edit")} onChanged={load} />
        </TabsContent>
        <TabsContent value="communications" className="mt-4">
          <CommunicationsPanel clientId={id} contacts={d.contacts} canWrite={can("client:edit")} />
        </TabsContent>
        {hasCommercial && (<>
          <TabsContent value="commercial" className="mt-4" data-testid="client-commercial-tab">
            <AgreementsPanel clientId={id} currency={c.currency} canWrite={can("client:edit")} />
            <Card className="p-5 mt-4">
            <div className="mt-1 font-display font-semibold mb-3 flex items-center gap-2"><Lock className="h-4 w-4 text-[#255058]" /> Rate Cards</div>
            <div className="space-y-2">{(d.rate_cards || []).map((r, i) => <div key={i} className="flex justify-between p-2.5 rounded-lg border border-border text-sm"><span className="font-medium">{r.role}</span><span>{money(r.bill_rate, c.currency)}/hr bill · {money(r.pay_rate, c.currency)}/hr pay</span></div>)}{!(d.rate_cards || []).length && <div className="text-sm text-muted-foreground">No rate cards.</div>}</div>
          </Card></TabsContent>
          <TabsContent value="revenue" className="mt-4"><Card className="overflow-hidden">{(d.invoices || []).length ? <div className="divide-y divide-border">{d.invoices.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 p-3 text-sm"><span className="font-mono text-xs">{inv.invoice_number}</span><span className="flex-1 text-muted-foreground">{money(inv.total, inv.currency)}</span><StatusBadge value={inv.status} /></div>
          ))}</div> : <div className="p-6 text-sm text-muted-foreground text-center">No invoices.</div>}</Card></TabsContent>
        </>)}
        <TabsContent value="documents" className="mt-4">
          <FileManager recordType="client" recordId={id} canWrite={can("client:edit")} title="Client Documents" />
        </TabsContent>
        <TabsContent value="activity" className="mt-4"><Card className="p-5"><div className="space-y-3">{d.activity.length ? d.activity.map((a) => (
          <div key={a.id} className="flex gap-3 text-sm"><div className="h-1.5 w-1.5 rounded-full bg-[#255058] mt-1.5 shrink-0" /><div><div>{a.summary}</div><div className="text-[11px] text-muted-foreground">{a.actor_name} · {timeAgo(a.created_at)}</div></div></div>
        )) : <div className="text-sm text-muted-foreground">No activity.</div>}</div></Card></TabsContent>
      </Tabs>
    </div>
  );
}
function Stat({ label, value }) { return <div><div className="text-xs text-muted-foreground">{label}</div><div className="font-display font-bold">{value}</div></div>; }
function Row({ k, v }) { return <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right">{v || "—"}</dd></div>; }
